import { useGT } from 'gt-react';
import { X } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import type { QueuedMessage } from '@/context/agent-state.context';

interface QueuedMessagesProps {
  messages: readonly QueuedMessage[];
  onWithdraw: (requestId: string) => void;
}

/**
 * Messages sent while the assistant is replying (AI-518), waiting for the
 * reply to take them. Each can be withdrawn until it is placed.
 */
export function QueuedMessages({ messages, onWithdraw }: QueuedMessagesProps) {
  const gt = useGT();
  if (messages.length === 0) return null;
  return (
    <ul
      className="mb-2 flex flex-col gap-1"
      aria-label={gt('Queued messages', {
        context: 'list of chat messages waiting for the assistant reply',
      })}
    >
      {messages.map((message) => (
        <li
          key={message.requestId}
          className="flex items-center gap-2 rounded-md border bg-muted/50 px-2 py-1 text-xs"
        >
          <span className="shrink-0 font-medium text-muted-foreground">
            {message.mode === 'steer'
              ? gt('After this step', {
                  context:
                    'label on a queued chat message that joins the reply after its current step',
                })
              : gt('Next', {
                  context:
                    'label on a queued chat message that runs after the current reply',
                })}
          </span>
          <span className="min-w-0 flex-1 truncate">{message.text}</span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-5 w-5 shrink-0 p-0"
            onClick={() => onWithdraw(message.requestId)}
            aria-label={gt('Withdraw queued message', {
              context: 'button that removes a queued chat message',
            })}
          >
            <X className="h-3 w-3" />
          </Button>
        </li>
      ))}
    </ul>
  );
}
